import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { ConfidenceBand } from '@/components/ConfidenceBand';
import { RecommendationChip } from '@/components/RecommendationChip';
import { InstrumentBezelIcon } from '@/components/identity';
import {
  assist,
  isDomainChoice,
  type AssistResponse,
  type DomainOption,
} from '@/data/copilot';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { MOCK_RECOMMENDATIONS } from '@/data/mock';
import { ApiError, USE_MOCK_DATA } from '@/lib/api';
import { engineForPath } from '@/engines/registry';
import { useLocation } from 'react-router-dom';
import { VoiceCopilot, AuraWaveform, type VoiceProviderConfig } from '@/voice';
import { getVoiceStatus } from '@/voice/net/voiceApi';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { ContentActionPanel, ApprovalActionPanel } from '@/voice/actions';
import { ContentGeneratePanel } from '@/components/ContentGeneratePanel';
// Aura publish P1 (owner decision 10-10-2026): "push the next N drafts to <site>"
// in the typed chat — cards on screen, Draft / Publish per card or for all.
import { BatchPublishPanel } from '@/voice/actions/BatchPublishPanel';
import { canManagePublishing, detectBatchPublishAsk } from '@/voice/actions/batchPublishModel';
import { useOptionalAuth } from '@/auth/AuthContext';
import { useBatchPublishVoice } from '@/voice/actions/useBatchPublishVoice';
import { defaultBatchPublishDeps } from '@/voice/actions/batchPublishDeps';
import { ConversationTurn } from '@/voice/wake/turnEntity';
// Aura P2 (owner GO 10-10-2026): "which 3 articles should we write?" in the typed chat.
import { SuggestWritePanel } from '@/voice/actions/SuggestWritePanel';
import { detectSuggestWriteAsk } from '@/voice/actions/suggestWriteModel';
import { useSuggestWriteVoice } from '@/voice/actions/useSuggestWriteVoice';
import { defaultSuggestWriteDeps } from '@/voice/actions/suggestWriteDeps';

/* ----------------------------------------------------------------------------
 * The Copilot Rail (signature moment §6.4) — an always-present, engine-aware
 * Signal-Copper assistant. The prompt box is LIVE: it POSTs /v1/ai/assist
 * (AuraGen AI service) with the active engine as context and renders the
 * answer as an advisory card — copper keyline, "proposed by copilot",
 * confidence band, explicit Advisory label. This slice is assist/answer only,
 * so the honest actions are Dismiss + Copy (Approve/Edit arrive with the
 * acting verbs later). Collapses to a bezel-and-notch orb (§2.4.1).
 * AI proposes; a human decides — the endpoint is advisory by construction.
 *
 * Voice: the main Copilot also offers Aura voice (push-to-talk), reachable
 * app-wide — not just Content Studio. It is a mode toggle over the same rail:
 * the text chat is preserved (VoiceCopilot keeps its own text fallback) and is
 * fail-closed disabled when voice_available is false.
 *
 * Responsive: desktop (>= lg) is the inline 320px right rail (unchanged). Below
 * lg the open rail becomes a bottom sheet (scrim, focus trap, ESC/overlay
 * close, safe-area padding); the collapsed orb honours the bottom-right inset.
 * -------------------------------------------------------------------------- */
export function CopilotRail({
  open,
  onToggle,
  seededQuery,
}: {
  open: boolean;
  onToggle: () => void;
  seededQuery?: string;
}) {
  const location = useLocation();
  const engine = engineForPath(location.pathname);
  // The shared active-domain context (Phase 2, Lane 2A): the selected
  // domain scopes Aura's answers and is shown as a "Viewing:" chip.
  const { active: activeDomain, select: selectDomain } = useActiveDomain();
  const [draft, setDraft] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [answer, setAnswer] = useState<AssistResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);

  // Aura voice capability probe (same fail-closed contract as Content Studio):
  // any non-true / error / loading state disables the launcher with an honest
  // tooltip and never attempts a live voice call.
  const voiceStatus = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => getVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const voiceAvailable = voiceStatus.data?.voice_available === true;
  const voiceConfig: VoiceProviderConfig = {
    stt: voiceStatus.data?.stt_provider ?? 'mock',
    tts: voiceStatus.data?.tts_provider ?? 'mock',
    ttsVoiceId: voiceStatus.data?.tts_voice_id ?? 'aura-2-pandora-en',
  };
  const voiceTip = voiceAvailable
    ? 'Talk to Aura'
    : 'Voice unavailable' + (voiceStatus.data?.reason ? ' — ' + voiceStatus.data.reason : '');
  // Voice mode hosts the voice surface in the rail; the text-chat views hide
  // (VoiceCopilot keeps its own text fallback) so text chat is never lost.
  const showChat = !(voiceMode && voiceAvailable);

  const isMobile = useIsMobile();
  const sheetOpen = open && isMobile;
  useBodyScrollLock(sheetOpen);
  const trapRef = useFocusTrap<HTMLElement>(sheetOpen, onToggle);

  // Recommendation chips stay demo-only until the recommend verb ships; in
  // real-data mode the rail is honest about having no suggestions yet.
  const allRecs = USE_MOCK_DATA ? MOCK_RECOMMENDATIONS : [];
  const contextRecs = engine ? allRecs.filter((r) => r.engineKey === engine.key) : allRecs;
  const recs = contextRecs.length ? contextRecs : allRecs;

  // Aura publish P1: text-only instance of the batch flow (no TTS, no mic). The
  // same closed-set rules as voice: a typed "publish"/"draft" answers the open
  // question, a bare "yes" gets "Publish or save as draft?", taps always work.
  const batchTurnRef = useRef(new ConversationTurn());
  const batchListenRef = useRef<(() => Promise<unknown>) | null>(null);
  // C6b (owner decision 10-10-2026): batch publishing is owner/operator only.
  const railAuth = useOptionalAuth();
  const railRole = railAuth?.activeWorkspace?.role;
  const batchFlow = useBatchPublishVoice({
    deps: defaultBatchPublishDeps,
    canManagePublishing: () => canManagePublishing(railRole),
    speak: async () => { /* chat rail is text-only: the line is shown on the card */ },
    turnRef: batchTurnRef,
    listenRef: batchListenRef,
    activeDomain: () => (activeDomain ? { projectId: activeDomain.projectId, hostname: activeDomain.hostname } : null),
  });
  // Aura P2: text-only suggest + write flow; on success the drafts move to the
  // batch cards above (same turn entity, so only one question is open at a time).
  const suggestListenRef = useRef<(() => Promise<unknown>) | null>(null);
  const suggestFlow = useSuggestWriteVoice({
    deps: defaultSuggestWriteDeps,
    speak: async () => { /* chat rail is text-only: the line is shown on the card */ },
    turnRef: batchTurnRef,
    listenRef: suggestListenRef,
    activeDomain: () => (activeDomain ? { projectId: activeDomain.projectId, hostname: activeDomain.hostname } : null),
    canWrite: () => canManagePublishing(railRole),
    onWritten: (site, ids, channel) => batchFlow.presentDrafts(site, ids, channel),
  });

  const runAssist = async (prompt: string, projectId?: number) => {
    if (!prompt || pending) return;
    if (suggestFlow.isAwaitingAnswer()) {
      setAsked(prompt);
      setDraft('');
      setAnswer(null);
      setError(null);
      await suggestFlow.handleAnswer(prompt, 'chat');
      return;
    }
    const suggestAsk = projectId === undefined ? detectSuggestWriteAsk(prompt) : null;
    if (suggestAsk) {
      setAsked(prompt);
      setDraft('');
      setAnswer(null);
      setError(null);
      await suggestFlow.begin(suggestAsk, 'chat');
      return;
    }
    if (batchFlow.isAwaitingAnswer()) {
      setAsked(prompt);
      setDraft('');
      setAnswer(null);
      setError(null);
      await batchFlow.handleAnswer(prompt, 'chat');
      return;
    }
    const batchAsk = projectId === undefined ? detectBatchPublishAsk(prompt) : null;
    if (batchAsk) {
      setAsked(prompt);
      setDraft('');
      setAnswer(null);
      setError(null);
      await batchFlow.begin(batchAsk, 'chat');
      return;
    }
    setAsked(prompt);
    setDraft('');
    setAnswer(null);
    setError(null);
    setCopied(false);
    setPending(true);
    try {
      const res = await assist(prompt, {
        engine: engine?.key ?? null,
        context: { route: location.pathname },
        // explicit chip choice wins; otherwise the shell's active domain
        // scopes the answer (server still re-validates ownership).
        projectId: projectId ?? activeDomain?.projectId ?? undefined,
      });
      setAnswer(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError('The copilot is catching its breath — try again in a minute.');
      } else if (e instanceof ApiError && e.status === 503) {
        setError('The copilot is not configured on this environment yet.');
      } else {
        setError('The copilot could not answer just now. Nothing was changed.');
      }
    } finally {
      setPending(false);
    }
  };

  const submit = () => runAssist(draft.trim());

  // Text/spoken helper: what to show/copy for either response shape.
  const answerText = (a: AssistResponse): string =>
    isDomainChoice(a) ? a.reply : a.display_text ?? a.proposal.text;

  // Pick a domain from the clarification chips: persist it (server + localStorage)
  // then RE-ISSUE the original question so Aura answers scoped to that domain.
  // Single-domain workspaces never reach this — the server auto-selects.
  const chooseDomain = async (d: DomainOption) => {
    const question = asked;
    setError(null);
    try {
      // select() persists server-side + updates the shared active-domain
      // context, so the shell selector reflects the choice immediately.
      await selectDomain(d.project_id);
    } catch {
      /* persistence best-effort; the request still scopes via project_id below */
    }
    if (question) {
      await runAssist(question, d.project_id);
    }
  };

  const dismiss = () => {
    setAnswer(null);
    setAsked(null);
    setError(null);
    setCopied(false);
  };

  const copy = async () => {
    if (!answer) return;
    try {
      await navigator.clipboard.writeText(answerText(answer));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  if (!open) {
    // collapsed bezel-and-notch orb — copper notch permanently "on" (§2.4.1),
    // subtle idle pulse (reduced-motion disables it via the global rule).
    // Fixed position respects the mobile safe-area inset (0 on desktop → same).
    return (
      <button
        onClick={onToggle}
        aria-label="Open copilot"
        style={{
          bottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
          right: 'calc(1.25rem + env(safe-area-inset-right))',
        }}
        className="fixed z-30 flex h-12 w-12 animate-orb-pulse items-center justify-center rounded-full border border-ai/30 bg-surface shadow-ai transition-transform duration-fast ease-calibration hover:scale-105"
      >
        <InstrumentBezelIcon glyph="✧" copperNotch size={26} className="text-ai" />
      </button>
    );
  }

  return (
    <>
      {/* mobile scrim — desktop never renders it (display:none via lg:hidden) */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-40 bg-scrim/50 animate-fade-in lg:hidden"
          onClick={onToggle}
          aria-hidden
        />
      )}
      <aside
        ref={trapRef}
        aria-label="Copilot"
        role={sheetOpen ? 'dialog' : undefined}
        aria-modal={sheetOpen ? true : undefined}
        tabIndex={sheetOpen ? -1 : undefined}
        className={cn(
          'flex flex-col bg-surface shadow-ai outline-none',
          // mobile bottom sheet
          'fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] rounded-t-2xl border-t border-ai/30 pb-[env(safe-area-inset-bottom)]',
          // desktop inline right rail — byte-equivalent to the original at >= lg
          'lg:static lg:inset-auto lg:z-auto lg:max-h-none lg:w-80 lg:shrink-0 lg:rounded-none lg:border-l lg:border-t-0 lg:pb-0',
        )}
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div className="flex items-center gap-2">
            <InstrumentBezelIcon glyph="✧" copperNotch size={22} className="text-ai" />
            <div className="leading-tight">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
                Copilot
              </div>
              {engine && (
                <div className="text-[11px] text-text-tertiary">{engine.name}</div>
              )}
            </div>
          </div>
          <button
            onClick={onToggle}
            aria-label="Collapse copilot"
            className="rounded-sm p-1 text-text-tertiary hover:bg-hover hover:text-text-primary max-lg:flex max-lg:h-11 max-lg:w-11 max-lg:items-center max-lg:justify-center max-lg:p-0"
          >
            <span aria-hidden className="lg:hidden">&#10005;</span>
            <span aria-hidden className="hidden lg:inline">⇥</span>
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <p className="text-xs text-text-tertiary">
            Context-aware. I propose; you decide. Every accepted action is audit-logged.
          </p>

          {/* shared active-domain context — the domain scoping Aura's SEO
              answers, mirrored from the shell's DomainSelector. */}
          {activeDomain && (
            <div className="inline-flex items-center gap-1 rounded-sm border border-ai/25 bg-canvas px-1.5 py-0.5 text-[10px] text-text-secondary">
              <span aria-hidden className="text-text-tertiary">◍</span>
              <span className="text-text-tertiary">Viewing:</span>
              <span className="font-medium text-text-primary">{activeDomain.hostname}</span>
            </div>
          )}

          {/* Aura voice — reachable app-wide from the main Copilot. Fail-closed:
              disabled with an honest tooltip when voice_available is false. */}
          {voiceMode && voiceAvailable ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setVoiceMode(false)}
                className="text-[11px] text-text-tertiary underline-offset-2 hover:underline"
              >
                ← Back to text chat
              </button>
              <VoiceCopilot config={voiceConfig} engine={engine?.key ?? null} />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => voiceAvailable && setVoiceMode(true)}
              disabled={!voiceAvailable}
              title={voiceTip}
              aria-label={voiceAvailable ? 'Talk to Aura by voice' : 'Voice unavailable'}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-all duration-fast',
                !voiceAvailable && 'cursor-not-allowed opacity-60',
              )}
              style={
                voiceAvailable
                  ? { color: '#f59e0b', background: '#f59e0b0d', border: '1.5px solid #f59e0b', boxShadow: '0 0 14px -3px #f59e0b59' }
                  : { color: '#7c828f', background: '#ffffff05', border: '1.5px solid #ffffff1a' }
              }
            >
              <AuraWaveform active={voiceAvailable} bars={4} heightPx={13} className={voiceAvailable ? undefined : 'opacity-60'} />
              {voiceAvailable ? 'Talk to Aura' : 'Voice unavailable'}
            </button>
          )}

          {showChat && (<>
          {seededQuery && !asked && (
            <div className="rounded-md border border-border-subtle bg-inset p-3 text-sm text-text-secondary">
              <span className="text-text-tertiary">You asked:</span> “{seededQuery}”
              <div className="ai-shimmer mt-2 h-2 w-2/3 rounded-full" />
            </div>
          )}

          {asked && (
            <div className="rounded-md border border-border-subtle bg-inset p-3 text-sm text-text-secondary">
              <span className="text-text-tertiary">You asked:</span> “{asked}”
            </div>
          )}

          {/* calibration-shimmer thinking state while the model composes */}
          {suggestFlow.view && (
            <SuggestWritePanel
              variant="rail"
              view={suggestFlow.view}
              onWrite={() => void suggestFlow.writeAll('tap')}
              onWriteOne={(i) => void suggestFlow.writeOne(i, 'tap')}
              onChooseSite={(id) => void suggestFlow.chooseSite(id)}
              onDismiss={suggestFlow.dismiss}
            />
          )}

          {batchFlow.view && (
            <BatchPublishPanel
              variant="rail"
              view={batchFlow.view}
              onConfirm={(mode, ids) => void batchFlow.confirm(mode, ids, 'tap')}
              onChooseSite={(id) => void batchFlow.chooseSite(id)}
              onUndo={(pubId, draftId) => void batchFlow.undo(pubId, draftId)}
              onDismiss={batchFlow.dismiss}
              onUpdateLive={(draftId) => void batchFlow.updateLive(draftId)}
              onConfirmUpdateLive={() => void batchFlow.confirmUpdateLive('tap')}
              onCancelUpdateLive={() => void batchFlow.cancelUpdateLive()}
              onUndoUpdateLive={() => void batchFlow.undoUpdateLive()}
            />
          )}

          {pending && (
            <div
              className="space-y-2 rounded-md border border-ai/25 bg-ai-wash/30 p-3"
              role="status"
              aria-label="Copilot is thinking"
            >
              <div className="ai-shimmer h-2 w-5/6 rounded-full" />
              <div className="ai-shimmer h-2 w-2/3 rounded-full" />
              <div className="ai-shimmer h-2 w-1/2 rounded-full" />
            </div>
          )}

          {error && !pending && (
            <div className="rounded-md border border-border-default bg-inset p-3 text-xs text-text-secondary">
              {error}
            </div>
          )}

          {/* domain-choice clarification — pick which connected domain to analyse.
              Rendered as selectable chips; picking one persists the active domain
              and re-issues the question so Aura answers scoped to that domain. */}
          {answer && !pending && isDomainChoice(answer) && (
            <div className="rounded-md border border-ai/40 bg-ai-wash/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
                  ✧ which domain?
                </span>
                <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
                  Advisory
                </span>
              </div>

              <p className="text-sm text-text-primary">{answer.reply}</p>

              <div className="mt-3 flex flex-wrap gap-2">
                {answer.domains.map((d) => (
                  <button
                    key={d.project_id}
                    onClick={() => void chooseDomain(d)}
                    title={d.hostname}
                    className="rounded-full border border-ai/40 bg-canvas px-3 py-1 text-xs font-medium text-text-primary transition-colors duration-fast hover:border-ai hover:bg-ai-wash/60"
                  >
                    {d.display_name || d.hostname}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex items-center justify-end border-t border-ai/15 pt-2">
                <span className="font-mono text-[9px] text-text-tertiary">BookedCentral Copilot</span>
              </div>
            </div>
          )}

          {/* advisory proposal card — the §6.4 tri-state "AI proposal" treatment.
              Rendered as clean short paragraphs (display_text is markdown-free);
              a "Viewing:" chip names the domain the answer is scoped to, if any. */}
          {answer && !pending && !isDomainChoice(answer) && (
            <div className="rounded-md border border-ai/40 bg-ai-wash/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
                  ✧ proposed by copilot
                </span>
                <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
                  Advisory
                </span>
              </div>

              {answer.domain_scope && (
                <div className="mb-2 inline-flex items-center gap-1 rounded-sm border border-ai/25 bg-canvas px-1.5 py-0.5 text-[10px] text-text-secondary">
                  <span className="text-text-tertiary">Viewing:</span>
                  <span className="font-medium text-text-primary">{answer.domain_scope.hostname}</span>
                </div>
              )}

              <div className="space-y-2 text-sm text-text-primary">
                {(answer.display_text ?? answer.proposal.text)
                  .split(/\n{2,}/)
                  .map((para, i) => (
                    <p key={i} className="whitespace-pre-line leading-relaxed">
                      {para.trim()}
                    </p>
                  ))}
              </div>

              <p className="mt-2 text-[11px] leading-snug text-text-secondary">
                <span className="font-semibold text-text-tertiary">Rationale · </span>
                {answer.proposal.rationale}
              </p>

              <div className="mt-3 flex items-center justify-between border-t border-ai/15 pt-2">
                <ConfidenceBand level={answer.proposal.confidence} />
                {/* platform branding only — never the underlying provider/model */}
                <span className="font-mono text-[9px] text-text-tertiary">
                  BookedCentral Copilot
                </span>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={copy}
                  className="rounded-sm border border-border-default px-2 py-1 text-[11px] text-text-secondary transition-colors duration-fast hover:bg-hover hover:text-text-primary"
                >
                  {copied ? 'Copied ✓' : 'Copy'}
                </button>
                <button
                  onClick={dismiss}
                  className="rounded-sm px-2 py-1 text-[11px] text-text-tertiary transition-colors duration-fast hover:bg-hover hover:text-text-primary"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Aura content-action flow (P3 Lane 3D): propose -> guided brief ->
              draft handoff. Connectors stay gated (drafts only). Preserves the
              assist + voice + domain-choice surfaces above untouched. */}
          <ContentActionPanel
            engineKey={engine?.key ?? null}
            routePath={location.pathname}
            activeProjectId={activeDomain?.projectId ?? null}
            onDomainSelected={selectDomain}
          />

          {/* GAP #1: direct keyword-targeted generation — Mode (i) tracked-
              keyword picker + Mode (ii) structured prompt. Draft-only; the
              shared server-side dedup refuses a duplicate 409 with a clear
              "already exists / published on <site>" notice. */}
          <ContentGeneratePanel activeProjectId={activeDomain?.projectId ?? null} />

          {/* Automation pilot (Option a, owner 2026-07-19): show the human EXACTLY
              what will be created before they approve. Renders nothing unless a
              pending approval exists AND the API returns it (ws=1 + ROLE_OWNER +
              active pilot are enforced server-side; this panel only reflects that). */}
          <ApprovalActionPanel />

          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
              Suggested next moves
            </div>
            {recs.length ? (
              recs.map((r) => (
                <RecommendationChip key={r.id} rec={r} onReview={() => undefined} />
              ))
            ) : (
              <p className="rounded-md border border-dashed border-border-default p-3 text-xs text-text-tertiary">
                No suggestions yet — the copilot proposes next moves once your engines have live
                activity to reason over.
              </p>
            )}
          </div>
          </>)}
        </div>

        {showChat && (
        <div className="border-t border-border-subtle p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="flex items-center gap-2 rounded-md border border-border-default bg-canvas px-3 py-2"
          >
            <span className="text-ai" aria-hidden>✧</span>
            <input
              aria-label="Ask the copilot"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={pending ? 'Thinking…' : 'Ask the copilot…'}
              disabled={pending}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-tertiary disabled:opacity-60"
            />
          </form>
          <div className={cn('mt-1.5 text-center text-[10px]', 'text-text-tertiary')}>
            Advisory only · I propose, you decide
          </div>
        </div>
        )}
      </aside>
    </>
  );
}
